/**
 * The Revenue page's read model (wave 18): `GET /api/v1/analytics/revenue/summary`,
 * `/trend`, `/breakdown` and `/timing` (`modules/revenue`, whose README is the contract;
 * `docs/architecture/revenue-definitions.md` is the prose). `analytics.read` for all four.
 *
 * Every route takes the page's `basis` (gross, or net of refunds), so the page can never
 * show one basis in one panel and the other in the next, and the global filters' period,
 * website, environments and comparison. Money arrives in integer minor units of
 * `meta.currency` and leaves in major units of the same currency, as the Overview's does;
 * ratios (shares, rates) stay fractions to four places, as the API rounds them.
 *
 * Every figure the API cannot give arrives as `null` and stays `null`, with the reason the
 * API names beside it: a screen shows "—" or "Not available · <reason>", never 0. Nothing
 * here derives a figure the API does not send.
 */

import { majorUnits } from '@/design/format'
import type { GlobalFilters } from '@/lib/filters'

import { overviewSearch, type OverviewMetaOut } from './overview'
import type { AttributionModel, Envelope } from './types'

// --- the vocabulary --------------------------------------------------------------------

/** `basis` on every route (decision 1): gross is the headline, net of refunds beside it. */
export const REVENUE_BASES = ['gross', 'net'] as const
export type RevenueBasis = (typeof REVENUE_BASES)[number]

export const REVENUE_BASIS_LABELS: Record<RevenueBasis, string> = {
  gross: 'Gross',
  net: 'Net of refunds',
}

/** The API's `compare`, from the filter bar's comparison. */
export type RevenueCompare = 'previous_period' | 'same_period_last_year' | 'none'

/** `granularity` on the trend: `hour` only for ranges of at most two days. */
export const REVENUE_GRANULARITIES = ['hour', 'day', 'week', 'month'] as const
export type RevenueGranularity = (typeof REVENUE_GRANULARITIES)[number]

/** The longest range the trend answers by the hour (`details.max_days` on its 422). */
export const HOURLY_MAX_DAYS = 2

/** `by` on the breakdown route. */
export const BREAKDOWN_DIMENSIONS = ['competition', 'prize_category', 'channel', 'campaign', 'customer_type', 'payment_method'] as const
export type BreakdownDimension = (typeof BREAKDOWN_DIMENSIONS)[number]

/** The breakdown's `limit`: 1–50, 10 by default. */
export const BREAKDOWN_MAX_LIMIT = 50

/**
 * The fixed rows (`definitions.py` `RevenueRowKind`), in the order the API lists them after
 * the values and `other` (`FIXED_ROW_ORDER`, which the code runs; the README's prose lists
 * them differently). The web reads the rows in the order they arrive and never re-sorts
 * them; this order is for the demo client to match.
 */
export const BREAKDOWN_FIXED_KINDS = [
  'other',
  'uncategorised',
  'unsynced_competition',
  'fees_and_other',
  'unclassified',
  'no_campaign',
  'unattributed',
  'unknown',
  'unmatched_refunds',
] as const
export type BreakdownFixedKind = (typeof BREAKDOWN_FIXED_KINDS)[number]
export type BreakdownKind = 'value' | BreakdownFixedKind

/** The summary's figures, in the README's order. */
export const SUMMARY_METRIC_KEYS = [
  'revenue_gross',
  'revenue_net',
  'revenue_net_before_refunds',
  'discounts',
  'orders',
  'orders_zero_value',
  'aov',
  'tickets_sold',
  'average_ticket_price',
  'tickets_per_order',
  'refunds',
  'refund_count',
  'refund_rate',
  'revenue_per_visitor',
] as const
export type SummaryMetricKey = (typeof SUMMARY_METRIC_KEYS)[number]

export type RevenueUnit = 'currency_minor' | 'count' | 'ratio'

/** Why a figure is null (`definitions.py`); `null_reason` and `previous_null_reason` alike. */
export type RevenueNullReason =
  | 'no_orders'
  | 'no_tickets'
  | 'no_revenue'
  | 'too_few_customers'
  | 'no_attributed_revenue'
  | 'range_too_short'

/** Why every `previous` figure is null (`meta.previous_unavailable`). */
export type PreviousUnavailable = 'currency_rate_unavailable' | 'insufficient_history' | 'range_overlaps_comparison' | 'not_compared'

/** Why a figure is not in this read model at all (`not_available`). */
export type RevenueNotAvailable = 'needs_browser_events' | 'needs_ad_platform'

// --- wire shapes (snake_case, as the API sends them) ----------------------------------

/** The four routes' `meta`: the Overview's, with the basis, the comparison asked for and why it is missing. */
export interface RevenueMetaOut extends Omit<OverviewMetaOut, 'previous_unavailable'> {
  basis: RevenueBasis
  compare: RevenueCompare
  previous_unavailable: PreviousUnavailable | null
}

interface DeltaOut {
  absolute: number
  ratio: number | null
}

export interface SummaryMetricOut {
  unit: RevenueUnit
  value: number | null
  previous: number | null
  delta: DeltaOut | null
  null_reason?: RevenueNullReason | null
  previous_null_reason?: RevenueNullReason | null
}

interface ConcentrationShareOut {
  unit: 'ratio'
  value: number | null
  previous: number | null
  null_reason?: RevenueNullReason | null
  previous_null_reason?: RevenueNullReason | null
}

export interface RevenueSummaryOut {
  data: {
    basis: RevenueBasis
    compare: RevenueCompare
    metrics: Record<SummaryMetricKey, SummaryMetricOut>
    not_available: Partial<Record<SummaryMetricKey, RevenueNotAvailable>>
    concentration: {
      top_decile_customers_share: ConcentrationShareOut & {
        customers: number
        previous_customers: number | null
        guest_share: number | null
        previous_guest_share: number | null
      }
      top_5_competitions_share: ConcentrationShareOut
      top_channel_share: ConcentrationShareOut & { channel: string | null; previous_channel: string | null }
    }
    site_credit: { status: 'included_unsplit'; reason: string }
  }
  meta: RevenueMetaOut
}

export interface TrendBucketOut {
  start: string
  end: string
  first_day: string
  last_day: string
  revenue: number
  orders: number
  tickets: number
  previous_start: string | null
  previous_revenue: number | null
}

interface TrendTotalsOut {
  revenue: number
  orders: number
  tickets: number
}

export interface TrendMarkerOut {
  type: 'launch' | 'draw'
  at: string
  source: 'start_at' | 'draw_at' | 'end_at'
  competition_id: string
  name: string
}

export interface RevenueSeriesOut {
  data: {
    basis: RevenueBasis
    compare: RevenueCompare
    granularity: RevenueGranularity
    buckets: TrendBucketOut[]
    totals: TrendTotalsOut
    previous_totals: TrendTotalsOut | null
    markers: TrendMarkerOut[]
  }
  meta: RevenueMetaOut
}

export interface BreakdownRowOut {
  key: string
  kind: BreakdownKind
  label: string
  revenue: number
  share: number | null
  orders: number
  aov: number | null
  aov_null_reason?: RevenueNullReason | null
  tickets: number
  previous_revenue: number | null
  delta: DeltaOut | null
}

export interface RevenueBreakdownOut {
  data: {
    by: BreakdownDimension
    basis: RevenueBasis
    compare: RevenueCompare
    model: AttributionModel | null
    limit: number
    rows: BreakdownRowOut[]
    total: { revenue: number; orders: number; tickets: number; previous_revenue: number | null }
    share_null_reason?: RevenueNullReason | null
  }
  meta: RevenueMetaOut
}

export interface TimingCellOut {
  weekday: number
  hour: number
  revenue: number
  orders: number
}

export interface WeekdayProfileOut {
  weekday: number
  average_revenue: number
  days: number
  previous_average_revenue: number | null
  previous_days: number | null
}

export interface RevenueTimingOut {
  data: {
    basis: RevenueBasis
    compare: RevenueCompare
    grid: TimingCellOut[]
    weekday_profile: WeekdayProfileOut[] | null
    weekday_profile_null_reason?: RevenueNullReason | null
    peak: { weekday: number; start_hour: number; hours: number; revenue: number; share: number | null } | null
    peak_null_reason?: RevenueNullReason | null
  }
  meta: RevenueMetaOut
}

// --- the view ---------------------------------------------------------------------------

/** What every answer's figures are framed by. */
export interface RevenueFrame {
  basis: RevenueBasis
  /** The currency every amount is in: `meta.currency`. */
  currency: string
  /** The tenant's timezone, which its days, hours and weekdays are in. */
  timezone: string
  /** The comparison range actually read (weekday-aligned, so not the calendar period before); null with none. */
  previousRange: { from: string; to: string } | null
  /** Why every `previous` figure is null; null when they are not. */
  previousUnavailable: PreviousUnavailable | null
}

/** A change: `absolute` in the figure's own unit (major units for money), `ratio` a fraction, null from 0. */
export interface RevenueDelta {
  absolute: number
  ratio: number | null
}

export interface RevenueFigure {
  unit: RevenueUnit
  /** Major units for money; a fraction for a ratio. */
  value: number | null
  previous: number | null
  delta: RevenueDelta | null
  nullReason: RevenueNullReason | null
  previousNullReason: RevenueNullReason | null
}

export interface ConcentrationShare {
  value: number | null
  previous: number | null
  nullReason: RevenueNullReason | null
  previousNullReason: RevenueNullReason | null
}

export interface RevenueSummary extends RevenueFrame {
  metrics: Record<SummaryMetricKey, RevenueFigure>
  notAvailable: Partial<Record<SummaryMetricKey, RevenueNotAvailable>>
  concentration: {
    topDecileCustomers: ConcentrationShare & { customers: number; guestShare: number | null }
    topFiveCompetitions: ConcentrationShare
    topChannel: ConcentrationShare & { channel: string | null }
  }
  /** Site credit is in every revenue figure, not split from cash yet (decision 2). */
  siteCreditIncludedUnsplit: boolean
}

export interface TrendBucket {
  /** The bucket's instants, with the tenant's UTC offset; `end` exclusive. */
  start: string
  end: string
  firstDay: string
  lastDay: string
  /** Major units, under the basis. */
  revenue: number
  /** Zero-value orders excluded. */
  orders: number
  tickets: number
  previousStart: string | null
  /** Null with no comparison, and for an hour a clock change repeats or skips. */
  previousRevenue: number | null
}

export interface TrendMarker {
  type: 'launch' | 'draw'
  at: string
  source: 'start_at' | 'draw_at' | 'end_at'
  competitionId: string
  name: string
}

export interface RevenueSeries extends RevenueFrame {
  granularity: RevenueGranularity
  buckets: TrendBucket[]
  totals: { revenue: number; orders: number; tickets: number }
  previousTotals: { revenue: number; orders: number; tickets: number } | null
  markers: TrendMarker[]
}

export interface BreakdownRow {
  key: string
  kind: BreakdownKind
  label: string
  revenue: number
  /** Of `total.revenue`; null on every row with `shareNullReason`. */
  share: number | null
  orders: number
  aov: number | null
  aovNullReason: RevenueNullReason | null
  tickets: number
  previousRevenue: number | null
  delta: RevenueDelta | null
}

export interface RevenueBreakdown extends RevenueFrame {
  by: BreakdownDimension
  model: AttributionModel | null
  /** Values best first, then `other`, then the fixed rows: the API's order. */
  rows: BreakdownRow[]
  total: { revenue: number; orders: number; tickets: number; previousRevenue: number | null }
  shareNullReason: RevenueNullReason | null
}

export interface TimingCell {
  /** Monday 0. */
  weekday: number
  /** Tenant wall clock. */
  hour: number
  revenue: number
  orders: number
}

export interface WeekdayProfileEntry {
  weekday: number
  averageRevenue: number
  days: number
  previousAverageRevenue: number | null
}

export interface RevenueTiming extends RevenueFrame {
  /** 168 cells, Monday 00:00 first. */
  grid: TimingCell[]
  weekdayProfile: WeekdayProfileEntry[] | null
  weekdayProfileNullReason: RevenueNullReason | null
  peak: { weekday: number; startHour: number; hours: number; revenue: number; share: number | null } | null
  peakNullReason: RevenueNullReason | null
}

// --- queries ----------------------------------------------------------------------------

export interface RevenueQuery {
  filters: GlobalFilters
  basis: RevenueBasis
}

export interface RevenueSeriesQuery extends RevenueQuery {
  granularity: RevenueGranularity
}

export interface RevenueBreakdownQuery extends RevenueQuery {
  by: BreakdownDimension
  limit?: number
  /** Read for `channel` and `campaign` only. */
  model?: AttributionModel
  /**
   * Days inside the filters' period to read instead of all of it (a week of a weekly
   * panel): `from`/`to` replace the period's, every other filter stands.
   */
  window?: { from: string; to: string }
  /** Ask for no comparison (`compare=none`) whatever the filters say: a panel that draws none need not pay for one. */
  withoutComparison?: boolean
}

export interface RevenueApi {
  revenueSummary(query: RevenueQuery): Promise<Envelope<RevenueSummary>>
  revenueSeries(query: RevenueSeriesQuery): Promise<Envelope<RevenueSeries>>
  revenueBreakdown(query: RevenueBreakdownQuery): Promise<Envelope<RevenueBreakdown>>
  revenueTiming(query: RevenueQuery): Promise<Envelope<RevenueTiming>>
}

export const REVENUE_PATHS = {
  summary: '/analytics/revenue/summary',
  trend: '/analytics/revenue/trend',
  breakdown: '/analytics/revenue/breakdown',
  timing: '/analytics/revenue/timing',
} as const

/** The filter bar's comparison, as the API names it. */
export function revenueCompare(filters: GlobalFilters): RevenueCompare {
  if (filters.compare === 'none') return 'none'
  return filters.compare === 'previous_year' ? 'same_period_last_year' : 'previous_period'
}

/** The Overview's scope (period, website, environments), the comparison and the basis: what every route reads. */
export function revenueSearch(query: RevenueQuery, now: Date): URLSearchParams {
  const params = new URLSearchParams(overviewSearch(query.filters, now))
  params.set('basis', query.basis)
  params.set('compare', revenueCompare(query.filters))
  return params
}

export function revenueSeriesSearch(query: RevenueSeriesQuery, now: Date): string {
  const params = revenueSearch(query, now)
  params.set('granularity', query.granularity)
  return params.toString()
}

export function revenueBreakdownSearch(query: RevenueBreakdownQuery, now: Date): string {
  const params = revenueSearch(query, now)
  if (query.window) {
    params.set('from', query.window.from)
    params.set('to', query.window.to)
  }
  if (query.withoutComparison) params.set('compare', 'none')
  params.set('by', query.by)
  if (query.limit !== undefined) params.set('limit', String(Math.min(BREAKDOWN_MAX_LIMIT, Math.max(1, Math.trunc(query.limit)))))
  if (query.model && (query.by === 'channel' || query.by === 'campaign')) params.set('model', query.model)
  return params.toString()
}

// --- mappers ----------------------------------------------------------------------------

function frameFromWire(meta: RevenueMetaOut): RevenueFrame {
  return {
    basis: meta.basis,
    currency: meta.currency,
    timezone: meta.timezone,
    previousRange: meta.previous_range,
    previousUnavailable: meta.previous_unavailable,
  }
}

function moneyOrNull(minor: number | null, currency: string): number | null {
  return minor === null ? null : majorUnits(minor, currency)
}

function deltaFromWire(delta: DeltaOut | null, money: boolean, currency: string): RevenueDelta | null {
  if (!delta) return null
  return { absolute: money ? majorUnits(delta.absolute, currency) : delta.absolute, ratio: delta.ratio }
}

function figureFromWire(wire: SummaryMetricOut, currency: string): RevenueFigure {
  const money = wire.unit === 'currency_minor'
  const amount = (value: number | null) => (money ? moneyOrNull(value, currency) : value)
  return {
    unit: wire.unit,
    value: amount(wire.value),
    previous: amount(wire.previous),
    delta: deltaFromWire(wire.delta, money, currency),
    nullReason: wire.null_reason ?? null,
    previousNullReason: wire.previous_null_reason ?? null,
  }
}

function shareFromWire(wire: ConcentrationShareOut): ConcentrationShare {
  return { value: wire.value, previous: wire.previous, nullReason: wire.null_reason ?? null, previousNullReason: wire.previous_null_reason ?? null }
}

export function revenueSummaryFromWire(body: RevenueSummaryOut): RevenueSummary {
  const { data, meta } = body
  const metrics = Object.fromEntries(
    SUMMARY_METRIC_KEYS.map((key) => [key, figureFromWire(data.metrics[key], meta.currency)]),
  ) as Record<SummaryMetricKey, RevenueFigure>
  const { top_decile_customers_share: decile, top_5_competitions_share: competitions, top_channel_share: channel } = data.concentration
  return {
    ...frameFromWire(meta),
    metrics,
    notAvailable: { ...data.not_available },
    concentration: {
      topDecileCustomers: { ...shareFromWire(decile), customers: decile.customers, guestShare: decile.guest_share },
      topFiveCompetitions: shareFromWire(competitions),
      topChannel: { ...shareFromWire(channel), channel: channel.channel },
    },
    siteCreditIncludedUnsplit: data.site_credit.status === 'included_unsplit',
  }
}

export function revenueSeriesFromWire(body: RevenueSeriesOut): RevenueSeries {
  const { data, meta } = body
  const money = (minor: number) => majorUnits(minor, meta.currency)
  const totals = (wire: TrendTotalsOut) => ({ revenue: money(wire.revenue), orders: wire.orders, tickets: wire.tickets })
  return {
    ...frameFromWire(meta),
    granularity: data.granularity,
    buckets: data.buckets.map((bucket) => ({
      start: bucket.start,
      end: bucket.end,
      firstDay: bucket.first_day,
      lastDay: bucket.last_day,
      revenue: money(bucket.revenue),
      orders: bucket.orders,
      tickets: bucket.tickets,
      previousStart: bucket.previous_start,
      previousRevenue: moneyOrNull(bucket.previous_revenue, meta.currency),
    })),
    totals: totals(data.totals),
    previousTotals: data.previous_totals ? totals(data.previous_totals) : null,
    markers: data.markers.map((marker) => ({
      type: marker.type,
      at: marker.at,
      source: marker.source,
      competitionId: marker.competition_id,
      name: marker.name,
    })),
  }
}

export function revenueBreakdownFromWire(body: RevenueBreakdownOut): RevenueBreakdown {
  const { data, meta } = body
  const money = (minor: number) => majorUnits(minor, meta.currency)
  return {
    ...frameFromWire(meta),
    by: data.by,
    model: data.model,
    rows: data.rows.map((row) => ({
      key: row.key,
      kind: row.kind,
      label: row.label,
      revenue: money(row.revenue),
      share: row.share,
      orders: row.orders,
      aov: moneyOrNull(row.aov, meta.currency),
      aovNullReason: row.aov_null_reason ?? null,
      tickets: row.tickets,
      previousRevenue: moneyOrNull(row.previous_revenue, meta.currency),
      delta: deltaFromWire(row.delta, true, meta.currency),
    })),
    total: {
      revenue: money(data.total.revenue),
      orders: data.total.orders,
      tickets: data.total.tickets,
      previousRevenue: moneyOrNull(data.total.previous_revenue, meta.currency),
    },
    shareNullReason: data.share_null_reason ?? null,
  }
}

export function revenueTimingFromWire(body: RevenueTimingOut): RevenueTiming {
  const { data, meta } = body
  const money = (minor: number) => majorUnits(minor, meta.currency)
  return {
    ...frameFromWire(meta),
    grid: data.grid.map((cell) => ({ weekday: cell.weekday, hour: cell.hour, revenue: money(cell.revenue), orders: cell.orders })),
    weekdayProfile: data.weekday_profile
      ? data.weekday_profile.map((entry) => ({
          weekday: entry.weekday,
          averageRevenue: money(entry.average_revenue),
          days: entry.days,
          previousAverageRevenue: moneyOrNull(entry.previous_average_revenue, meta.currency),
        }))
      : null,
    weekdayProfileNullReason: data.weekday_profile_null_reason ?? null,
    peak: data.peak
      ? { weekday: data.peak.weekday, startHour: data.peak.start_hour, hours: data.peak.hours, revenue: money(data.peak.revenue), share: data.peak.share }
      : null,
    peakNullReason: data.peak_null_reason ?? null,
  }
}
